import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Table, Tooltip } from 'antd'
import _ from 'lodash'

import { colours } from '@qflow/theme'

import {
  DetailRow,
  AttachmentsSection,
  ValueAdd
} from 'portal/components/RecordDetails'
import { AttachmentsHeader } from 'portal/components/RecordDetails/AttachmentsHeader'

import {
  AlertsPanel,
  RemoveRecordButton,
  EditRecordButton,
  RecordHistoryButton,
  LineItemButtons,
  AddLineItemButton,
  InfractionFlag,
  ViewInAdminButton
} from 'portal/components/RecordsModules/common'
import {
  MainSection,
  MultiColumn,
  MultiColumnRow,
  SubSection,
  SubSectionHeading,
  StyledLabel,
  TableSection,
  NegativeMargin,
  NameText,
  ExpanderClose,
  ExpanderOpen
} from 'portal/components/RecordsModules/common/StyledComponents'
import { ActionStrip } from 'portal/components/RecordsModules/Edit/ActionStrip'

import {
  convertAssignedToArrayToString,
  DateFormatFull,
  imperial
} from 'portal/lib/functions'
import {
  useCountryDrivenValues,
  useModuleIds,
  useUserProjectRole
} from 'portal/lib/hooks'
import { ColourButton, Flex, SubHeading, Spacer } from 'portal/lib/primitives'
import { ChevronDown, EditPencil } from 'portal/lib/primitives/icons'
import { useInfractionGetter } from 'portal/store/infractions/InfractionsContext'
import { trackMixpanel } from 'portal/store/mixpanel/actions'

import { WasteManagementRoutes } from './WasteManagementRoutes'

export function Waste({ record }) {
  const [expandedRowKeys, setExpandedRowKeys] = useState()
  const [actionStripRecordID, setActionStripRecordID] = useState([])
  const [recordAssigneeMapping, setRecordAssigneeMapping] = useState({})

  const { accountId, moduleName, projectId } = useModuleIds()

  const { isProjectAdmin } = useUserProjectRole(accountId, projectId)
  const { text, wasteFields } = useCountryDrivenValues()

  const dispatch = useDispatch()

  const getInfraction = useInfractionGetter()

  const projectTimeZone = useSelector(
    state => state.projectDetails.modules?.timeZone
  )

  const projectCountry = useSelector(
    state => state.projectDetails.modules?.country
  )

  const projectIsAlertsEnabled = useSelector(
    state => state.projectDetails.modules?.isAlertsEnabled
  )

  const isImperial = imperial(projectCountry)

  let assignedTo = ''

  if (record.assignedTo) {
    assignedTo = convertAssignedToArrayToString(record.assignedTo)
  }

  const openActionStrip = () => {
    if (record && record.recordId) {
      setActionStripRecordID([record.recordId])
    }
  }

  const closeActionStrip = () => {
    setActionStripRecordID([])
  }

  // Prepare assigned to mapping for action strip
  useEffect(() => {
    if (record && record.recordId) {
      // Assuming you have a way to get assignee mapping for the record
      const newObject = {}
      newObject[record.recordId] = record.assignedTo

      setRecordAssigneeMapping(newObject)
    }
  }, [record])

  const handleApplyAssignments = useCallback(() => {
    const EV_ASSIGNEES_UPDATED = 'frontend_record_details_assignees_updated'
    dispatch(
      trackMixpanel(EV_ASSIGNEES_UPDATED, {
        project_id: projectId,
        account_id: accountId,
        record_id: record.id,
        record_type: moduleName
      })
    )
    setTimeout(() => {
      window.location.reload()
    }, 100)
  }, [accountId, dispatch, moduleName, projectId, record.id])

  const dataForItemsTable = useMemo(() => {
    let items = []
    if (record?.data?.items) {
      const originalItems = [...record.data.items]
      originalItems.forEach((element, index) => {
        items.push({
          originalIndex: index,
          managementRouteSourceDataYear:
            record.data.managementRouteSourceDataYear,
          ...element
        })
      })
      items = items.filter(p => !p.isDeleted)
    }
    if (record?.dataSourceTracking?.items) {
      items = items.map(item => {
        const dataSourceItem =
          record.dataSourceTracking.items[item.originalIndex]
        if (dataSourceItem) {
          return {
            ...item,
            valueAdd: dataSourceItem
          }
        }
        return item
      })
    }
    return items
  }, [record])

  // Set table rows to be expanded
  useEffect(() => {
    if (dataForItemsTable) {
      const keys = dataForItemsTable
        .map(item => {
          if (
            item.wasteManagementRoutes &&
            item.wasteManagementRoutes.length > 0 &&
            !item.isRemoved
          ) {
            return item.id
          }
          return null
        })
        .filter(p => p !== null)
      if (keys && keys.length < 3) {
        setExpandedRowKeys(keys)
      } else {
        setExpandedRowKeys([])
      }
    }
  }, [dataForItemsTable])

  const itemTableHeaders = useMemo(() => {
    const handleExpanderClick = id => {
      if (expandedRowKeys.includes(id)) {
        setExpandedRowKeys(expandedRowKeys.filter(p => p !== id))
      } else {
        setExpandedRowKeys([...expandedRowKeys, id])
      }
    }

    const columns = [
      {
        key: 'knownContainerType-ValueAdd',
        dataIndex: 'valueAdd',
        render: (item, record) => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{
                  fieldName: 'knownContainerType',
                  value: record.knownContainerType,
                  ...item.knownContainerType
                }}
              />
            </NegativeMargin>
          )
        },
        width: '30px'
      },
      {
        key: 'knownContainerType',
        title: 'Container Type',
        dataIndex: 'knownContainerType',
        render: (item, record) => {
          const infractions = getInfraction(
            `data.items[${record.originalIndex}]`,
            {
              isPrefix: true
            }
          )

          return (
            <>
              {record.isRemoved ? item : <NameText>{item}</NameText>}
              {infractions?.length > 0 && (
                <Tooltip
                  key={`infraction-${record.originalIndex}`}
                  title={infractions[0].eventDescriptor}
                  styles={{ body: { width: 'max-content' } }}
                >
                  <span
                    style={{ display: 'inline-block', marginLeft: '0.25rem' }}
                  >
                    <InfractionFlag
                      key={`infraction-flag-${record.originalIndex}`}
                    />
                  </span>
                </Tooltip>
              )}

              <br />
              {expandedRowKeys?.includes(record.id) &&
                record.wasteManagementRoutes &&
                record.wasteManagementRoutes.length > 0 && (
                  <ExpanderClose onClick={() => handleExpanderClick(record.id)}>
                    <span>See less</span>{' '}
                    <ChevronDown size={'8'} rotate={180} />
                  </ExpanderClose>
                )}
              {!expandedRowKeys?.includes(record.id) &&
                record.wasteManagementRoutes &&
                record.wasteManagementRoutes.length > 0 && (
                  <ExpanderOpen onClick={() => handleExpanderClick(record.id)}>
                    <span>View waste management route</span>{' '}
                    <ChevronDown size={'8'} />
                  </ExpanderOpen>
                )}
            </>
          )
        },
        width: '16rem'
      },
      wasteFields.wasteDescription && {
        key: 'wasteDescription-ValueAdd',
        dataIndex: 'valueAdd',
        render: (item, record) => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{
                  fieldName: 'wasteDescription',
                  value: record.wasteDescription,
                  ...item.wasteDescription
                }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      wasteFields.wasteDescription && {
        key: 'wasteDescription',
        title: 'Waste Description',
        dataIndex: 'wasteDescription'
      },
      wasteFields.wasteCode && {
        key: 'wasteCode-ValueAdd',
        dataIndex: 'valueAdd',
        render: (item, record) => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{
                  fieldName: 'wasteCode',
                  value: record.wasteCode,
                  ...item.wasteCode
                }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      wasteFields.wasteCode && {
        key: 'wasteCode',
        title: 'Waste Code (EWC)',
        dataIndex: 'wasteCode'
      },
      {
        key: 'volume-ValueAdd',
        dataIndex: 'valueAdd',
        render: (item, record, index) => {
          if (!record.volume && record.volumeEstimated) {
            return (
              <NegativeMargin>
                <ValueAdd
                  details={{
                    fieldName: 'volumeEstimated',
                    value: record.volumeEstimated,
                    ...item.volumeEstimated
                  }}
                />
              </NegativeMargin>
            )
          }
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{
                  fieldName: 'volume',
                  value: record.volume,
                  ...item.volume
                }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      {
        key: 'volume',
        title: isImperial ? (
          'Volume'
        ) : (
          <>
            Volume{' '}
            <span>
              (m<sup>3</sup>)
            </span>
          </>
        ),
        dataIndex: 'volume',
        render: (item, record, index) => {
          if (!item && record.volumeEstimated) {
            return `${record.volumeEstimated}${isImperial && (Array.isArray(record.volumeEstimated) ? record.volumeEstimated.length > 0 : record.volumeEstimated) ? ' Cubic yard' : ''}`
          }
          return `${item != null ? item : ''}${isImperial && (Array.isArray(item) ? item.length > 0 : item) ? ' Cubic yard' : ''}`
        }
      },
      {
        key: 'weight-ValueAdd',
        dataIndex: 'valueAdd',
        render: (item, record, index) => {
          if (!record.weight && record.weightEstimated) {
            return (
              <NegativeMargin>
                <ValueAdd
                  details={{
                    fieldName: 'weightEstimated',
                    value: record.weightEstimated,
                    ...item.weightEstimated
                  }}
                />
              </NegativeMargin>
            )
          }
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{
                  fieldName: 'weight',
                  value: record.weight,
                  ...item.weight
                }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      {
        key: 'weight',
        title: isImperial ? (
          'Weight'
        ) : (
          <>
            Weight <span>(t)</span>
          </>
        ),
        dataIndex: 'weight',
        render: (item, record, index) => {
          if (!item && record.weightEstimated) {
            return `${record.weightEstimated}${isImperial && (Array.isArray(record.weightEstimated) ? record.weightEstimated.length > 0 : record.weightEstimated) ? ' lbs' : ''}`
          }
          return `${item != null ? item : ''}${isImperial && (Array.isArray(item) ? item.length > 0 : item) ? ' lbs' : ''}`
        }
      },
      {
        key: 'quantity-ValueAdd',
        dataIndex: 'valueAdd',
        render: (item, record) => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{
                  fieldName: 'quantity',
                  value: record.quantity,
                  ...item.quantity
                }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      { key: 'quantity', title: 'Quantity', dataIndex: 'quantity' },
      {
        key: 'carbonKgCO2e-ValueAdd',
        dataIndex: 'valueAdd',
        render: (item, record) => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{
                  fieldName: 'carbonKgCO2e',
                  value: record.carbonKgCO2e,
                  ...item.carbonKgCO2e
                }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      {
        key: 'carbonKgCO2e',
        title: (
          <StyledLabel>
            <div>
              Carbon{' '}
              <span>
                (kg CO<sub>2</sub>e)
              </span>
            </div>
          </StyledLabel>
        ),
        render: item =>
          item.carbonKgCO2e ? _.round(item.carbonKgCO2e, 2) : null
      },
      {
        key: 'actions',
        title: '',
        render: (item, product, index) =>
          !item.isDeleted && record.data?.items[item.originalIndex] ? (
            <div style={{ display: 'flex' }}>
              <LineItemButtons
                record={record}
                index={item.originalIndex}
                isImperial={isImperial}
              />
            </div>
          ) : (
            `${item.originalIndex}`
          ),
        width: '90px',
        style: { display: 'flex' }
      }
    ].filter(Boolean) // Remove any columns that are false (for conditional columns)
    return columns
  }, [
    expandedRowKeys,
    getInfraction,
    isImperial,
    record,
    wasteFields.wasteCode,
    wasteFields.wasteDescription
  ])

  return (
    <>
      <MainSection>
        <ActionStrip
          recordIds={actionStripRecordID}
          recordAssigneeMapping={recordAssigneeMapping}
          onClose={closeActionStrip}
          onApply={handleApplyAssignments}
        />
        <Flex.Row amount={0} verticalCentre>
          <SubHeading
            id="WasteDetailHeading"
            isChild
            style={{ fontSize: '1.125rem', color: colours.CORE_NAVY }}
          >
            Waste Record
          </SubHeading>

          <Flex.By amount={1} />
          {record.canEdit && (
            <Flex.By amount={0}>
              <ViewInAdminButton
                record={record}
                accountId={accountId}
                moduleName={moduleName}
              />
              {record.qcrAppliedToRaw && (
                <>
                  <Spacer.Tiny />
                  <RecordHistoryButton />
                </>
              )}
              <Spacer.Tiny />
              <EditRecordButton />
              <Spacer.Tiny />
              <RemoveRecordButton />
            </Flex.By>
          )}
        </Flex.Row>

        <MultiColumn>
          <MultiColumnRow>
            <SubSection style={{ marginTop: '1.25rem' }}>
              <SubSectionHeading>General</SubSectionHeading>
              <DetailRow
                label="Assigned to"
                content={assignedTo}
                testId={'assigned-to'}
                valueAdd={{
                  fieldName: 'assignedTo',
                  ...record.dataSourceTracking?.assignedTo
                }}
                specialAction={
                  isProjectAdmin && (
                    <Tooltip title="Edit assignees">
                      <ColourButton.BasicBorderless
                        css={{
                          'width': 'fit-content',
                          'padding': '1px 5px',
                          'minHeight': '28px',
                          'color': colours.TEAL,
                          '&:hover': {
                            color: colours.TEAL_HOVER,
                            backgroundColor: colours.LIGHT_TEAL
                          }
                        }}
                        type="button"
                        onClick={openActionStrip}
                      >
                        <EditPencil />
                      </ColourButton.BasicBorderless>
                    </Tooltip>
                  )
                }
              />

              <DetailRow
                label="Record created"
                content={
                  record.createdAt
                    ? new Date(record.createdAt).toLocaleString(
                        [],
                        DateFormatFull(projectTimeZone)
                      )
                    : null
                }
              />

              <DetailRow
                label="Arrival date and time"
                content={new Date(record.data.removalDate).toLocaleString(
                  [],
                  DateFormatFull(projectTimeZone)
                )}
                valueAdd={{
                  fieldName: 'removalDate',
                  ...record.dataSourceTracking?.removalDate
                }}
              />

              <DetailRow
                label="Exit date and time"
                content={
                  record.data.removalExitDate
                    ? new Date(record.data.removalExitDate).toLocaleString(
                        [],
                        DateFormatFull(projectTimeZone)
                      )
                    : null
                }
                valueAdd={{
                  fieldName: 'removalExitDate',
                  ...record.dataSourceTracking?.removalExitDate
                }}
              />

              {wasteFields && wasteFields.consignmentId && (
                <DetailRow
                  label={text.consignmentNumber}
                  content={record.data.consignmentId}
                  valueAdd={{
                    fieldName: 'consignmentId',
                    ...record.dataSourceTracking?.consignmentId
                  }}
                />
              )}

              {wasteFields && wasteFields.receiverReferenceNumber && (
                <DetailRow
                  label="Sender reference"
                  content={record.data.receiverReferenceNumber}
                  valueAdd={{
                    fieldName: 'receiverReferenceNumber',
                    ...record.dataSourceTracking?.receiverReferenceNumber
                  }}
                />
              )}

              {wasteFields && wasteFields.subcontractorName && (
                <DetailRow
                  label="Subcontractor"
                  content={record.data.subcontractorName}
                  valueAdd={{
                    fieldName: 'subcontractorName',
                    ...record.dataSourceTracking?.subcontractorName
                  }}
                />
              )}

              {wasteFields && wasteFields.originSiteAddressKnown && (
                <DetailRow
                  label="Origin site address"
                  content={record.data.originSiteAddressKnown}
                  valueAdd={{
                    fieldName: 'originSiteAddressKnown',
                    ...record.dataSourceTracking?.originSiteAddressKnown
                  }}
                />
              )}

              {wasteFields && wasteFields.classificationCode && (
                <DetailRow
                  label="SIC code"
                  content={record.data.classificationCode}
                  valueAdd={{
                    fieldName: 'classificationCode',
                    ...record.dataSourceTracking?.classificationCode
                  }}
                />
              )}

              {wasteFields && wasteFields.classificationCodePresented && (
                <DetailRow
                  label="SIC code provided"
                  content={
                    record.data.classificationCodePresented ? 'Yes' : 'No'
                  }
                  valueAdd={{
                    fieldName: 'classificationCodePresented',
                    ...record.dataSourceTracking?.classificationCodePresented
                  }}
                />
              )}

              {wasteFields && wasteFields.vehicleRegistration && (
                <DetailRow
                  label="Vehicle reg"
                  content={record.data.vehicleRegistration}
                  valueAdd={{
                    fieldName: 'vehicleRegistration',
                    ...record.dataSourceTracking?.vehicleRegistration
                  }}
                />
              )}
            </SubSection>
            <SubSection>
              <SubSectionHeading>{text.carrier}</SubSectionHeading>

              {wasteFields && wasteFields.carrierName && (
                <DetailRow
                  label={text.carrierName}
                  content={record.data.carrierName}
                  valueAdd={{
                    fieldName: 'carrierName',
                    ...record.dataSourceTracking?.carrierName
                  }}
                />
              )}

              {wasteFields && wasteFields.supplierReferenceNumber && (
                <DetailRow
                  label="Reference"
                  content={record.data.supplierReferenceNumber}
                  valueAdd={{
                    fieldName: 'supplierReferenceNumber',
                    ...record.dataSourceTracking?.supplierReferenceNumber
                  }}
                />
              )}

              {wasteFields && wasteFields.carrierLicenceId && (
                <DetailRow
                  label="Licence"
                  content={record.data.carrierLicenceId}
                  valueAdd={{
                    fieldName: 'carrierLicenceId',
                    ...record.dataSourceTracking?.carrierLicenceId
                  }}
                />
              )}
            </SubSection>
            <SubSection>
              <SubSectionHeading>Facility</SubSectionHeading>
              {wasteFields && wasteFields.facilityOperatorName && (
                <DetailRow
                  label="Operator"
                  content={record.data.facilityOperatorName}
                  valueAdd={{
                    fieldName: 'facilityOperatorName',
                    ...record.dataSourceTracking?.facilityOperatorName
                  }}
                />
              )}

              {wasteFields && wasteFields.facilitySiteAddress && (
                <DetailRow
                  label="Site address"
                  content={record.data.facilitySiteAddress}
                  valueAdd={{
                    fieldName: 'facilitySiteAddress',
                    ...record.dataSourceTracking?.facilitySiteAddress
                  }}
                />
              )}

              {wasteFields && wasteFields.facilityPermitId && (
                <DetailRow
                  label="Permit"
                  content={record.data.facilityPermitId}
                  valueAdd={{
                    fieldName: 'facilityPermitId',
                    ...record.dataSourceTracking?.facilityPermitId
                  }}
                />
              )}

              {wasteFields && wasteFields.facilityPermitPresented && (
                <DetailRow
                  label="Permit provided"
                  content={record.data.facilityPermitPresented ? 'Yes' : 'No'}
                  valueAdd={{
                    fieldName: 'facilityPermitPresented',
                    ...record.dataSourceTracking?.facilityPermitPresented
                  }}
                />
              )}

              {wasteFields && wasteFields.clientReference && (
                <DetailRow
                  label="External reference"
                  content={record.data.clientReference}
                  valueAdd={{
                    fieldName: 'clientReference',
                    ...record.dataSourceTracking?.clientReference
                  }}
                />
              )}

              {wasteFields && wasteFields.notes && (
                <DetailRow
                  label="Notes"
                  content={record.data.notes}
                  valueAdd={{
                    fieldName: 'notes',
                    ...record.dataSourceTracking?.notes
                  }}
                />
              )}
            </SubSection>
            <SubSection>
              <SubSectionHeading>Journey</SubSectionHeading>
              <DetailRow
                label="Journey distance (mi)"
                content={
                  record.data.journey?.distanceMiles
                    ? _.round(record.data.journey.distanceMiles, 2).toString()
                    : null
                }
                tooltip="Journey distance represents the laden journey only"
                valueAdd={{
                  fieldName: 'distanceMiles',
                  ...record.dataSourceTracking?.journey
                }}
              />
              <DetailRow
                label={
                  <span>
                    Laden journey emissions (kg CO<sub>2</sub>e)
                  </span>
                }
                content={
                  record.data.journey?.ladenEmissionsKgCO2e
                    ? _.round(
                        record.data.journey.ladenEmissionsKgCO2e,
                        2
                      ).toString()
                    : null
                }
                valueAdd={{
                  fieldName: 'ladenEmissionsKgCO2e',
                  ...record.dataSourceTracking?.journey
                }}
              />
              <DetailRow
                label={
                  <span>
                    Unladen journey emissions (kg CO<sub>2</sub>e)
                  </span>
                }
                content={
                  record.data.journey?.unladenEmissionsKgCO2e
                    ? _.round(
                        record.data.journey.unladenEmissionsKgCO2e,
                        2
                      ).toString()
                    : null
                }
                valueAdd={{
                  fieldName: 'unladenEmissionsKgCO2e',
                  ...record.dataSourceTracking?.journey
                }}
              />
            </SubSection>
          </MultiColumnRow>
          {projectIsAlertsEnabled && (
            <AlertsPanel alerts={record.alerts} canEdit={record.canEdit} />
          )}
        </MultiColumn>
      </MainSection>

      <MainSection>
        <AttachmentsHeader record={record} />
        <AttachmentsSection record={record} attachments={record.attachments} />
      </MainSection>

      <MainSection>
        <Flex.Row
          amount={0}
          verticalCentre
          style={{ justifyContent: 'space-between' }}
        >
          <SubHeading
            isChild
            style={{ fontSize: '1.125rem', color: colours.CORE_NAVY }}
          >
            Items
          </SubHeading>

          {record.canEdit && <AddLineItemButton />}
        </Flex.Row>

        <TableSection>
          <Table
            id="items-table"
            dataSource={dataForItemsTable ?? []}
            rowKey={row => row.id}
            columns={itemTableHeaders}
            style={{ borderCollapse: 'separate', borderSpacing: '0 5px' }}
            pagination={false}
            locale={{ emptyText: 'No items' }}
            onRow={(record, index) => ({
              style: record.isRemoved
                ? {
                    textDecoration: 'line-through',
                    color: colours.BLUEY_GREY
                  }
                : {},
              className: expandedRowKeys?.includes(record.id)
                ? 'ant-table-expanded-row'
                : ''
            })}
            expandable={{
              expandedRowRender: record => (
                <WasteManagementRoutes
                  data={record.wasteManagementRoutes}
                  source={record.wasteManagementRoutesSource}
                  sourceYear={record.managementRouteSourceDataYear}
                />
              ),
              expandedRowKeys: expandedRowKeys,
              rowExpandable: record =>
                record.wasteManagementRoutes &&
                record.wasteManagementRoutes.length > 0,
              showExpandColumn: false
            }}
          />
        </TableSection>
      </MainSection>
    </>
  )
}

Waste.propTypes = {
  record: PropTypes.object.isRequired
}
